<script setup lang="ts">
import { CoButton } from '@deputy/copilot2';
import { useTimesheetStore, EventSources } from '@/stores/timesheets';
import { useRosterStore } from '@/stores/rosters';
import { useKioskConfigStore } from '@/stores/kioskConfig';
import { useTimesheetSetupStore } from '@/stores/timesheetSetup';
import { getTranslations } from '@/lib/util/i18n';
import { useAnalyticsService } from '@/lib/util/analytics/useAnalyticsService';
import { displayFromSeconds } from '@/lib/util/dateTime';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'shift-actions',
});

const rosters = useRosterStore();
const timesheets = useTimesheetStore();
const timesheetSetup = useTimesheetSetupStore();
const kioskConfig = useKioskConfigStore();
const analytics = useAnalyticsService();

const emit = defineEmits<{
  (e: 'startShift'): void;
  (e: 'showBreaks'): void;
  (e: 'showAreas'): void;
  (e: 'showEndShiftInformation'): void;
  (e: 'showEndBreakConfirmation'): void;
  (e: 'startBreak'): void;
}>();

function trackStartShift() {
  analytics.track(
    { name: 'SHIFT_START_STARTED', screen: 'HOME' },
    {
      source: EventSources.StartShift,
      shift: {
        details: timesheets.getShiftActionAnalyticsPayload(),
      },
    }
  );
}

function hasOnlyOneUnpaidBreak() {
  return (
    timesheets.getUnfinishedUnpaidScheduledBreaks().length == 1 &&
    timesheets.getUnfinishedPaidScheduledBreaks().length == 0
  );
}

function minimumStartTime() {
  const SECONDS_IN_MINUTE = 60;

  return displayFromSeconds(
    rosters.getStartRosterTime()! -
      +timesheetSetup.data.LocationSettings.CAN_CLOCKIN_SHIFT_EARLIER_MINS *
        SECONDS_IN_MINUTE
  );
}
</script>

<template>
  <div class="__actions">
    <div v-if="!timesheets.isOnShift" class="__before-shift">
      <CoButton
        class="__start-shift"
        type="primary"
        size="large"
        v-if="!rosters.getNextRosterForActiveUser()"
        @click="
          () => {
            trackStartShift();
            emit('showAreas');
          }
        "
      >
        {{ t('start-unscheduled-shift') }}
      </CoButton>
      <CoButton
        class="__start-shift"
        type="primary"
        size="large"
        :disabled="timesheetSetup.isClockInUnavailable()"
        v-else
        @click="
          () => {
            trackStartShift();
            emit('startShift');
          }
        "
      >
        {{
          timesheetSetup.isClockInUnavailable()
            ? t('disabled-start-shift', { minimumTime: minimumStartTime() })
            : t('start-scheduled-shift')
        }}
      </CoButton>
      <span
        v-if="
          rosters.getNextRosterForActiveUser() &&
          timesheetSetup.isClockInUnavailable()
        "
        class="__early-warning"
      >
        {{
          t('early-warning', {
            time: +timesheetSetup.data.LocationSettings
              .CAN_CLOCKIN_SHIFT_EARLIER_MINS,
            locationName: kioskConfig.data.KioskCompanyNameRaw,
          })
        }}
      </span>
    </div>

    <div class="__during-shift" v-else>
      <CoButton
        class="__start-break"
        type="primary"
        size="large"
        v-if="!timesheets.isOnBreak"
        @click="
          hasOnlyOneUnpaidBreak() ? emit('startBreak') : emit('showBreaks')
        "
      >
        {{ t('start-break') }}
      </CoButton>
      <CoButton
        class="__end-break"
        type="primary"
        size="large"
        :disabled="timesheetSetup.isEndBreakUnavailable()"
        v-if="timesheets.activeTimesheet && timesheets.isOnBreak"
        @click="emit('showEndBreakConfirmation')"
      >
        {{
          timesheetSetup.isEndBreakUnavailable()
            ? t('disabled-end-break', {
                minimumTime: displayFromSeconds(
                  timesheets.getActiveBreak()?.intUnixEnd
                ),
              })
            : t('end-break')
        }}
      </CoButton>
      <CoButton
        class="__end-shift"
        type="destructive"
        size="large"
        @click="emit('showEndShiftInformation')"
      >
        {{ t('end-shift') }}
      </CoButton>
    </div>
  </div>
</template>

<style scoped>
.__actions div {
  display: flex;
  align-items: stretch;
  gap: 1rem;
}
.__actions .__during-shift {
  flex-direction: row;
}

.__actions .__before-shift {
  flex-direction: column;
}

.__actions .__before-shift span {
  text-align: center;
  color: var(--co-shift-empty-border);
}

.__actions div * {
  flex: 1;
  margin: 0;
}
</style>
